interface CheckDomainResponse {
  sso: boolean;
  organizationId?: string;
  nonce?: string;
}

interface SessionInfo {
  authenticated: boolean;
  userId?: string;
  email?: string;
  identityId?: string;
  authMethod?: string;
}

interface ExchangeSessionResponse {
  success: boolean;
  error?: string;
}

const getApiBaseUrl = (): string => {
  // Prefer region-specific API URLs based on country code, then fall back
  const countryCode = (window as any).__COUNTRY_CODE__;

  if (countryCode) {
    const envKey = `REACT_APP_API_${String(countryCode).toUpperCase()}`;
    const envVal = process.env[envKey as keyof typeof process.env];
    if (envVal) {
      return envVal.replace(/\/$/, '');
    }
  }

  if (process.env.REACT_APP_API_IRL) {
    return process.env.REACT_APP_API_IRL.replace(/\/$/, '');
  }

  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  return '';
};

export const ssoAuthService = {
  async checkDomain(email: string): Promise<CheckDomainResponse> {
    try {
      const baseUrl = getApiBaseUrl();
      const res = await fetch(`${baseUrl}/auth/check-domain`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
        credentials: 'include',
      });
      if (!res.ok) {
        return { sso: false };
      }
      return await res.json();
    } catch {
      // Fail open — if API unreachable, fall back to password
      return { sso: false };
    }
  },

  redirectToSso(nonce: string): void {
    const baseUrl = getApiBaseUrl();
    window.location.href = `${baseUrl}/auth/sso?nonce=${encodeURIComponent(nonce)}`;
  },

  async logout(): Promise<void> {
    try {
      const baseUrl = getApiBaseUrl();
      await fetch(`${baseUrl}/auth/session/logout`, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
      });
    } catch {
      // Best-effort logout — even if the API call fails, the client
      // should clear its local state
    }
  },

  async exchangeSession(cognitoToken: string): Promise<ExchangeSessionResponse> {
    try {
      const baseUrl = getApiBaseUrl();
      const res = await fetch(`${baseUrl}/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cognitoToken }),
        credentials: 'include',
      });
      if (!res.ok) {
        return { success: false, error: `Session exchange failed: ${res.status}` };
      }
      return { success: true };
    } catch (err) {
      return { success: false, error: 'Session exchange request failed' };
    }
  },

  async validateSession(): Promise<SessionInfo> {
    try {
      const baseUrl = getApiBaseUrl();
      const res = await fetch(`${baseUrl}/auth/session`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) {
        return { authenticated: false };
      }
      return await res.json();
    } catch {
      return { authenticated: false };
    }
  },
};

export default ssoAuthService;
